import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface PwaContextValue {
  readonly online: boolean;
  readonly offlineReady: boolean;
  readonly updateAvailable: boolean;
  readonly installAvailable: boolean;
  readonly installed: boolean;
  readonly install: () => Promise<boolean>;
  readonly applyUpdate: () => Promise<void>;
  readonly dismissUpdate: () => void;
}

const PwaContext = createContext<PwaContextValue | null>(null);

export function PwaProvider({ children }: { readonly children: ReactNode }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent>();
  const [installed, setInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches);
  const {
    offlineReady: [offlineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) window.setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });

  useEffect(() => {
    const setConnected = (): void => setOnline(true);
    const setDisconnected = (): void => setOnline(false);
    const capturePrompt = (event: Event): void => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const markInstalled = (): void => {
      setInstalled(true);
      setInstallPrompt(undefined);
    };
    window.addEventListener('online', setConnected);
    window.addEventListener('offline', setDisconnected);
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', markInstalled);
    return () => {
      window.removeEventListener('online', setConnected);
      window.removeEventListener('offline', setDisconnected);
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', markInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!installPrompt) return false;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setInstallPrompt(undefined);
    return choice.outcome === 'accepted';
  }, [installPrompt]);

  const value = useMemo<PwaContextValue>(() => ({
    online,
    offlineReady,
    updateAvailable: needRefresh,
    installAvailable: Boolean(installPrompt) && !installed,
    installed,
    install,
    applyUpdate: async () => { await updateServiceWorker(true); },
    dismissUpdate: () => setNeedRefresh(false),
  }), [online, offlineReady, needRefresh, installPrompt, installed, install, updateServiceWorker, setNeedRefresh]);

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

export function usePwa(): PwaContextValue {
  const context = useContext(PwaContext);
  if (!context) throw new Error('usePwa must be used inside PwaProvider');
  return context;
}
